import { RefresherEventDetail } from "@ionic/core";
import {
  IonButtons,
  IonCard,
  IonContent,
  IonHeader,
  IonPage,
  IonRefresher,
  IonRefresherContent,
  IonTitle,
  IonToolbar,
} from "@ionic/react";
import { Box, Stack } from "@mui/material";
import { OpenNotificationCenterIcon } from "@src/app/notificationCenter/openNotificationCenterIcon";
import { getOpenNegotiationsCount } from "@src/app/rateNegotiation/api";
import { ActionType } from "@src/app/store/session/session.model";
import { UnverifiedShiftsListCard } from "@src/app/unverifiedShifts/unverifiedShiftListCard";
import { Agent } from "@src/appV2/Agents";
import { TemporaryBookedCasesWithMockServer } from "@src/appV2/ExperimentalHomeHealth/BookedCases/TemporaryBookedCasesWithMockServer";
import { useIsHomeHealthEnabled } from "@src/appV2/ExperimentalHomeHealth/lib/useIsHomeHealthEnabled";
import { useGetChatChannels } from "@src/appV2/Facilities/Chat/api/useGetChatChannels";
import { FacilityChatMessagesButton } from "@src/appV2/Facilities/Chat/MessagesButton";
import { CbhFeatureFlag, useCbhFlag } from "@src/appV2/FeatureFlags";
import { logEvent } from "@src/appV2/lib/analytics";
import { useToast } from "@src/appV2/lib/Notifications";
import { NotificationCenterButton } from "@src/appV2/NotificationCenter";
import { useExtraWorkedTimeRequests } from "@src/appV2/Shifts/MandatoryBreakPolicy/api/useExtraWorkedTimeRequests";
import { EmptyMyShiftsPlaceholder } from "@src/appV2/Shifts/MyShifts/components/EmptyMyShiftsPlaceholder";
import { PendingSentHomeRequestsCard } from "@src/appV2/Shifts/MyShifts/components/PendingSentHomeRequestsCard";
import { RateNegotiationsCard } from "@src/appV2/Shifts/MyShifts/components/RateNegotiationsCard";
import { ShiftCard } from "@src/appV2/Shifts/MyShifts/components/ShiftCard";
import { UnverifiedShiftsCard } from "@src/appV2/Shifts/MyShifts/components/UnverifiedShiftsCard";
import { type Shift as ShiftV2 } from "@src/appV2/Shifts/Shift/types";
import { PendingShiftInvitesCount } from "@src/appV2/Shifts/ShiftInvites/PendingShiftInvitesCount";
import { logApiFailureEvent } from "@src/lib/analytics";
import { ShiftLoader } from "@src/lib/deprecatedCode";
import { Facility, Shift } from "@src/lib/interface";
import { useAppSelector } from "@store/index";
import { IUnverifiedShiftState } from "@store/shift/model";
import { Store } from "@store/store.model";
import { sumBy } from "lodash";
import moment from "moment-timezone";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

import { CBHShiftItem } from "./cbhShiftItem";
import { NoMyShiftsAvailable } from "./emptyState";
import { HeaderMessagesIcon } from "./headerMessagesIcon";
import { getShiftKeys } from "./utils";
import { USER_EVENTS } from "../../constants";
import { api } from "../api";
import { fetchRecentColleagues } from "../components/workWithFriends/api";
import { FCMRequestData } from "../pendingSentHomeRequests/interfaces";
import { PendingSentHomeRequestsCount } from "../pendingSentHomeRequests/pendingSentHomeRequestsCount";
import { RateNegotiationBanner } from "../rateNegotiation/negotiationsBanner";
import { updateRecentColleaguesList } from "../store/recentColleagues/actions";
import { getAgentUnVerifiedShifts, setFcmRequestsData } from "../store/shift/actions";
import { UnverifiedShiftsCount } from "../unverifiedShifts/unverifiedShiftsCount";

interface ShiftsListProps {
  shifts: (Shift | ShiftV2)[];
  onRefresh: () => Promise<void>;
  isLoading: boolean;
}

export function ShiftsList(props: ShiftsListProps) {
  const { shifts, onRefresh, isLoading: shiftsLoading } = props;
  const pageTitle = "My Shifts";

  const [isLoading, setLoading] = useState(true);
  const [activeNegotiationsCount, setActiveNegotiationsCount] = useState<number>(0);
  const [facilityProposalsCount, setFacilityProposalsCount] = useState<number>(0);

  const dispatch = useDispatch();
  const location = useLocation();
  const searchParameters = new URLSearchParams(location.search);
  const [fcmRequests, setFcmRequests] = useState<FCMRequestData[]>([]);

  const agent = useAppSelector((state) => state?.session?.agent ?? {});

  const isMyShiftsRefactorEnabled = useCbhFlag(CbhFeatureFlag.ENABLE_MY_SHIFTS_REFACTOR, {
    defaultValue: false,
  });

  const isChatEnabled = useCbhFlag(CbhFeatureFlag.CHAT, {
    defaultValue: false,
  });
  const { showErrorToast } = useToast();

  const updateRecentColleagues = useCallback(async () => {
    dispatch(updateRecentColleaguesList(null, true));
    const workers = (await fetchRecentColleagues()) ?? [];
    dispatch(updateRecentColleaguesList(workers, false));
  }, [dispatch]);

  const dispatchUnverifiedShifts = () => {
    dispatch(getAgentUnVerifiedShifts());
  };

  const isNotificationCenterEnabled = useCbhFlag(CbhFeatureFlag.NOTIFICATION_CENTER_HCP_MOBILE, {
    defaultValue: true,
  });

  const unverifiedShiftsByDate = useSelector(
    (state: Store) => state.shiftStore.unverifiedShiftsByDate
  );
  const unverifiedShifts = useMemo(
    () => Object.values(unverifiedShiftsByDate).flat(),
    [unverifiedShiftsByDate]
  );

  const { data: extraWorkedTimeRequestsData } = useExtraWorkedTimeRequests();
  const extraWorkedTimeRequests = extraWorkedTimeRequestsData?.data ?? [];

  const requestUnverifiedList = async () => {
    dispatchUnverifiedShifts();
  };

  const requestNegotiations = async () => {
    try {
      const { totalNegotiatingShiftsCount, negotiationsWaitingOnWorker } =
        await getOpenNegotiationsCount();
      setActiveNegotiationsCount(totalNegotiatingShiftsCount);
      setFacilityProposalsCount(negotiationsWaitingOnWorker);
      dispatch({
        type: ActionType.UPDATE_AGENT,
        data: {
          agent: {
            negotiationsCount: {
              totalNegotiatingShiftsCount,
              negotiationsWaitingOnWorker,
            },
          },
        },
      });
    } catch (error) {
      logApiFailureEvent(error);
      showErrorToast("Error while getting open negotiations");
    }
  };

  const fetchShiftsAndNegotiations = async () => {
    requestUnverifiedList();
    await requestNegotiations();
  };

  const getFcmRequests = async () => {
    try {
      const fcmRequestsResponse = await api.shift.fetchSentHomeRequests();
      setFcmRequests(fcmRequestsResponse);
      dispatch(setFcmRequestsData(fcmRequestsResponse));
    } catch (err) {
      showErrorToast("Could not load pending sent home requests, please Refresh the page again!");
    }
  };

  useEffect(
    () => {
      const fetchData = async () => {
        setLoading(true);
        await fetchShiftsAndNegotiations();
        await getFcmRequests();
        setLoading(false);
      };
      fetchData();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      /**
       * FIXME - convert to react-query.
       * This useEffect should not exist.
       */
    ]
  );

  useEffect(() => {
    if (!agent.hideAsColleagueOnShifts) {
      updateRecentColleagues();
    }
  }, [agent, updateRecentColleagues]);

  const { isLoading: isLoadingChatChannels, data: chatChannels } = useGetChatChannels();

  const doRefresh = async (event: CustomEvent<RefresherEventDetail>) => {
    setLoading(true);
    await Promise.all([fetchShiftsAndNegotiations(), onRefresh()]);
    setLoading(false);
    event.detail.complete();
  };

  const isNotFCMshift = (shift: Shift | ShiftV2) => {
    if ("_id" in shift) {
      return !fcmRequests.some((fcmRequest) => fcmRequest.shift._id === shift._id);
    }
    return true;
  };

  const missingTimesheetsShifts = unverifiedShifts.filter((shift) => {
    if (shift?.timecardNotAvailable?.reason) {
      return false;
    } else if ((shift?.timecard?.files?.length ?? 0) > 0) {
      return false;
    } else if (!shift?.facility?.verificationPreference?.usesTimesheets) {
      return false;
    } else {
      const shiftVerificationTimeout = shift?.businessrules?.value;
      if (shiftVerificationTimeout) {
        return !moment(shift.end).add(shiftVerificationTimeout, "hours").isBefore();
      } else {
        return true;
      }
    }
  });

  const missingTimesheetsShiftsCount = missingTimesheetsShifts.length;
  const isHomeHealthEnabled = useIsHomeHealthEnabled();

  return (
    <IonPage>
      <IonHeader color="primary" no-border>
        <IonToolbar>
          {isMyShiftsRefactorEnabled && isNotificationCenterEnabled ? (
            <IonButtons slot="start">
              <NotificationCenterButton />
            </IonButtons>
          ) : (
            <OpenNotificationCenterIcon logIconViewedEvent={true} />
          )}
          <IonTitle class="ion-no-padding ion-text-center">{pageTitle}</IonTitle>
          {isMyShiftsRefactorEnabled && isChatEnabled ? (
            <IonButtons slot="end">
              <FacilityChatMessagesButton />
            </IonButtons>
          ) : (
            <HeaderMessagesIcon agentId={agent._id} />
          )}
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <IonRefresher slot="fixed" onIonRefresh={doRefresh}>
          <IonRefresherContent />
        </IonRefresher>
        {activeNegotiationsCount > 0 &&
          (isMyShiftsRefactorEnabled ? (
            <RateNegotiationBanner
              activeNegotiationsCount={activeNegotiationsCount}
              facilityProposalsCount={facilityProposalsCount}
              fetchShiftsAndNegotiations={fetchShiftsAndNegotiations}
              deepLinkNegotiationId={searchParameters.get("negotiationId")}
            />
          ) : (
            <Box padding={2}>
              <RateNegotiationsCard
                activeNegotiationsCount={activeNegotiationsCount}
                facilityProposalsCount={facilityProposalsCount}
                deepLinkNegotiationId={searchParameters.get("negotiationId") ?? undefined}
              />
            </Box>
          ))}
        {isMyShiftsRefactorEnabled && fcmRequests.length > 0 && (
          <Box sx={{ padding: 2 }}>
            <PendingSentHomeRequestsCard
              pendingSentHomeRequestsCount={fcmRequests.length}
              totalEarnings={sumBy(fcmRequests, (fcmRequest) => fcmRequest.earnings)}
            />
          </Box>
        )}
        {!isMyShiftsRefactorEnabled && fcmRequests.length > 0 && (
          <PendingSentHomeRequestsCount fcmRequestsData={fcmRequests} />
        )}
        {(unverifiedShifts.length > 0 || extraWorkedTimeRequests.length > 0) &&
          (isMyShiftsRefactorEnabled ? (
            <Box padding={2} paddingBottom={4}>
              <UnverifiedShiftsCard
                unverifiedShiftsCount={unverifiedShifts.length}
                missingTimesheetsCount={missingTimesheetsShiftsCount}
                extraWorkedTimeRequestsCount={extraWorkedTimeRequests.length}
              />
            </Box>
          ) : (
            <UnverifiedShiftsCount
              agentId={agent._id}
              unverifiedShiftsData={unverifiedShifts}
              extraWorkedTimeRequestsCount={extraWorkedTimeRequests.length}
            />
          ))}
        <PendingShiftInvitesCount />
        {!shifts.length && <ShiftLoader loading={isLoading || shiftsLoading} count={1} />}
        {!shifts.length &&
          !isLoading &&
          !shiftsLoading &&
          (isMyShiftsRefactorEnabled ? (
            <Box margin={2}>
              <EmptyMyShiftsPlaceholder />
            </Box>
          ) : (
            <NoMyShiftsAvailable unverifiedShiftsLength={unverifiedShifts.length} />
          ))}
        {!isLoadingChatChannels &&
          shifts.filter(isNotFCMshift).map((shift) => {
            if (isMyShiftsRefactorEnabled) {
              return (
                <Box key={(shift as Shift)._id} sx={{ margin: 2 }}>
                  {/**
                   * We force this conversion because the old Shift type is not entirely compatible
                   * with ShiftV2. For example, in ShiftV1, all properties are set to optional which
                   * conflicts with ShiftV2 where a bit more of thought was put on what was optional
                   * and what was not.
                   *
                   * Also, as part of this work https://linear.app/clipboardhealth/project/v2-route-migrations-315992c867a9/FEF
                   * this whole file will be eventually migrated to appV2, so this forced conversion
                   * has an expiration date already.
                   */}
                  <ShiftCard
                    agent={agent as unknown as Agent}
                    shift={shift as unknown as ShiftV2}
                    chatChannels={chatChannels ?? []}
                  />
                </Box>
              );
            }

            return (
              <ShiftItemSwitcher
                // Eventually, all these casts will go away when this route is refactored.
                key={(shift as Shift)._id}
                shift={shift as Shift}
                agentId={agent._id}
                dispatchUnverifiedShifts={dispatchUnverifiedShifts}
                onClickOnItemFacilityDetails={() => {
                  logEvent(USER_EVENTS.VIEWED_FACILITY_PROFILE, {
                    bookingType: shift.isInstantBook ? "instant book" : "standard",
                  });
                  logEvent(USER_EVENTS.SHIFT_CARD_FACILITY_PROFILE_CLICKED, {
                    bookingType: shift.isInstantBook ? "instant book" : "standard",
                  });
                }}
                unverifiedShiftsByDate={unverifiedShiftsByDate}
              />
            );
          })}
        {isHomeHealthEnabled && (
          <Stack paddingX={3}>
            <TemporaryBookedCasesWithMockServer />
          </Stack>
        )}
      </IonContent>
    </IonPage>
  );
}

interface IShiftItemSwitcherProps {
  shift: Shift;
  onClickOnItemFacilityDetails: (
    shift: Shift,
    facility: Facility,
    displayExtraTimePayCard?: boolean
  ) => void;
  dispatchUnverifiedShifts: () => void;
  unverifiedShiftsByDate: IUnverifiedShiftState;
  agentId?: string;
}

function ShiftItemSwitcher({
  shift,
  onClickOnItemFacilityDetails,
  dispatchUnverifiedShifts,
  unverifiedShiftsByDate,
  agentId,
}: IShiftItemSwitcherProps) {
  const dispatch = useDispatch();
  const { date } = getShiftKeys(shift.start);
  const unverifiedShift = unverifiedShiftsByDate?.[date]?.find((s) => s._id === shift._id);
  if (unverifiedShift) {
    return (
      <IonCard
        mode="ios"
        button
        className="ion-padding no-text-transform"
        key={unverifiedShift._id}
      >
        <UnverifiedShiftsListCard
          reload={dispatchUnverifiedShifts}
          shift={unverifiedShift}
          openOverlay={(shiftDetailsForUpload: Shift) => {
            dispatch({
              type: ActionType.SHOW_UPLOAD_TIMESHEET_MODAL,
              data: { shiftDetailsForUpload },
            });
          }}
        />
      </IonCard>
    );
  }

  const isShiftOngoing = moment().isAfter(shift.start);
  if (isShiftOngoing && !shift.agent) {
    return null;
  }
  return (
    <CBHShiftItem
      key={shift._id}
      onClickOnItemFacilityDetails={onClickOnItemFacilityDetails}
      shift={shift}
      agentId={agentId}
    />
  );
}
